import React, { useState } from 'react';

import { Box, Button, Header, Modal, Pagination, SpaceBetween, Table } from '@amzn/awsui-components-react-v3';

import { useMetrics } from 'appSelectorContext';
import { isEmpty } from 'lodash';

import { METRIC_PUBLISHER_NAMES } from '../../constants';
import { Receive, ReceiveReferenceIdType, ReceiveTypeNameMap, ReferenceQueryResponseItem } from '../../store/types';
import { DUPLICATE_SHIPMENTS_METRIC } from './constants';

export enum ReferenceColumnSelection {
  PO = 'PO',
  ASN = 'ASN',
  PO_MPN_ASN = 'PO_MPN_ASN',
}

interface MultipleReferencesProps {
  references: ReferenceQueryResponseItem[];
  onDismiss?: () => void;
  onConfirm?: (reference: any) => void;
  referenceValue?: string;
  title?: string;

  withPagination?: boolean;
  pagesCount?: number;
  pageIndx?: number;
  openEnd?: boolean;
  loading?: boolean;
  onUpdatePage?: (pageNumber: number, pageSize?: number) => void;

  visibleColumns?: string[];
}

const Columns = [
  {
    id: 'externalReferenceValue',
    header: 'Reference ID',
    cell: (e: Receive) => e?.externalReferenceValue,
  },
  {
    id: 'shipmentId',
    header: 'Shipment ID',
    cell: (e: Receive) => e?.externalReferenceValue,
  },
  {
    id: 'externalReferenceType',
    header: 'Reference Type',
    cell: (e: Receive) => ReceiveTypeNameMap[e?.externalReferenceType],
  },
  {
    id: 'carrier',
    header: 'Carrier',
    cell: (e: Receive) => e?.carrier,
  },
  {
    id: 'supplier',
    header: 'Supplier',
    cell: (e: Receive) => e?.supplier,
  },
  {
    id: 'trackingId',
    header: 'Tracking ID',
    cell: (e: Receive) => e?.trackingId,
  },
  {
    id: 'sitename',
    header: 'Site Name',
    cell: (e: Receive) => e?.siteName,
  },
  {
    id: 'receiveReferenceId',
    header: 'Receive Reference ID',
    cell: (e: Receive) => e?.receiveReferenceId,
  },
];

export const MultipleReferences = ({
  references = [],
  pageIndx = 0,
  onDismiss,
  onConfirm,
  referenceValue,
  withPagination = false,
  pagesCount = 1,
  title,
  openEnd = false,
  loading = false,
  onUpdatePage,
  visibleColumns = ['carrier', 'supplier', 'trackingId', 'sitename', 'externalReferenceValue', 'externalReferenceType'],
}: MultipleReferencesProps) => {
  const context = useMetrics();
  const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
    METRIC_PUBLISHER_NAMES.SCAN_SHIPMENT,
  );
  const [visible, setVisible] = useState(true);
  const [selectedItems, setSelectedItems] = React.useState<Receive[]>([]);

  const onChangePage = ({ detail }) => {
    onUpdatePage && onUpdatePage(detail?.currentPageIndex);
  };
  const onNextPageClick = ({ detail }) => {
    onUpdatePage && onUpdatePage(detail?.requestedPageIndex);
  };
  const onPreviousPageClick = ({ detail }) => {
    onUpdatePage && onUpdatePage(detail?.requestedPageIndex);
  };

  const isPO = references.some(
    (ref: ReferenceQueryResponseItem) => ref.scannableIdFoundAs === ReceiveReferenceIdType.ORDER_NUMBER,
  );

  const items = references.map((r) => r?.receiveReference);

  return (
    <Modal
      onDismiss={() => {
        setVisible(false);
        onDismiss && onDismiss();
      }}
      visible={visible}
      size="large"
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              ariaLabel="Cancel IPN Selection dialog"
              onClick={() => {
                setVisible(false);
                onDismiss && onDismiss();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              ariaLabel="Confirm selected IPN"
              disabled={selectedItems.length === 0}
              onClick={() => {
                setVisible(false);
                const reference = references.find(
                  (ref) => ref?.receiveReference?.receiveReferenceId === selectedItems?.[0]?.receiveReferenceId,
                );
                publisher.publishCounterMonitor(DUPLICATE_SHIPMENTS_METRIC, 1);
                onConfirm && selectedItems?.length > 0 && onConfirm(reference);
              }}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={
        <Header variant="h2" actions={null}>
          {!isEmpty(title) ? title : `Multiple ${isPO ? "Order's" : "Shipment's"} found with ${referenceValue}`}
        </Header>
      }
    >
      <Table
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        selectedItems={selectedItems}
        loading={loading}
        ariaLabels={{
          allItemsSelectionLabel: ({ selectedItems }) =>
            `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`,
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter((i) => i.receiveReferenceId === item.receiveReferenceId).length;
            return `${item.carrier} is ${isItemSelected ? '' : 'not'} selected`;
          },
          tableLabel: `Total ${items} Multiple References`,
        }}
        data-table={`multiple-search-references`}
        onRowClick={({ detail }: any) => {
          setSelectedItems([detail.item]);
        }}
        loadingText="Loading references"
        pagination={
          withPagination ? (
            <Pagination
              currentPageIndex={pageIndx}
              pagesCount={pagesCount}
              openEnd={openEnd}
              onChange={onChangePage}
              onNextPageClick={onNextPageClick}
              onPreviousPageClick={onPreviousPageClick}
              ariaLabels={{
                nextPageLabel: 'nextPage',
                pageLabel: (pageNumber: number) => `pageNumber-${pageNumber}`,
                paginationLabel: 'referencesPagination',
                previousPageLabel: 'previousPage',
              }}
            />
          ) : null
        }
        columnDefinitions={Columns}
        items={items}
        selectionType="single"
        visibleColumns={visibleColumns}
      />
    </Modal>
  );
};
